import React from "react";
import { Box, IconButton } from "@mui/material";
import { PlusIconBlue, RemoveIcon } from "@/assets/images";
import HPButton from "@/components/common/Button";
import HPInput from "@/components/common/Input";
import HPDropdown from "@/components/common/Dropdown";
import { useGetDashboardClientsQuery } from "@/store/api/dashboard-api";

interface NewAccount {
  name: string;
  email: string;
  company: string;
}

interface InviteNewAccountProps {
  newAccounts: NewAccount[];
  handleAccountChange: (
    index: number,
    field: keyof { name: string; email: string; company: string },
    value: string,
  ) => void;
  handleAddAccount: () => void;
  handleRemoveAccount: (index: number) => void;
  validationErrors: Record<
    number,
    { name?: string; email?: string; company?: string }
  >;
  dashboardId: string;
}

const InviteNewAccount: React.FC<InviteNewAccountProps> = ({
  newAccounts,
  handleAccountChange,
  handleAddAccount,
  handleRemoveAccount,
  validationErrors,
  dashboardId,
}) => {
  const { data: clientsData, isLoading: areClientsLoading } =
    useGetDashboardClientsQuery(dashboardId, {
      skip: !dashboardId,
    });

  const clientOptions = React.useMemo(() => {
    if (areClientsLoading || !clientsData) {
      return [];
    }

    const allClients = [];
    if (clientsData.primaryClient) {
      allClients.push(clientsData.primaryClient);
    }
    if (clientsData.additionalClients) {
      allClients.push(...clientsData.additionalClients);
    }

    return allClients.map((client) => ({
      label: client.name,
      value: client.id,
    }));
  }, [clientsData, areClientsLoading]);

  const canRemove = newAccounts.length > 1;

  return (
    <Box p={3}>
      {newAccounts.map((account, index) => (
        <Box
          key={index}
          display="grid"
          gridTemplateColumns={canRemove ? "1fr 1fr 1fr 5px" : "1fr 1fr 1fr"}
          gap={2}
          mb={2}
        >
          <HPInput
            label="Name"
            placeholder="Enter full name"
            value={account.name}
            onChange={(e) => handleAccountChange(index, "name", e.target.value)}
            error={!!validationErrors[index]?.name}
            helperText={validationErrors[index]?.name}
          />
          <HPInput
            label="Email"
            placeholder="Enter email"
            value={account.email}
            onChange={(e) =>
              handleAccountChange(index, "email", e.target.value)
            }
            error={!!validationErrors[index]?.email}
            helperText={validationErrors[index]?.email}
          />
          <HPDropdown
            label="Client company"
            value={account.company}
            onChange={(value) => handleAccountChange(index, "company", value)}
            options={clientOptions}
            disabled={areClientsLoading}
            error={!!validationErrors[index]?.company}
            helperText={validationErrors[index]?.company}
            placeholder="Select client company"
            multiSelect={false}
          />
          {canRemove && (
            <Box
              display="flex"
              alignItems="center"
              justifyContent="center"
              paddingLeft={"10px"}
              paddingTop={"20px"}
            >
              <IconButton
                onClick={() => handleRemoveAccount(index)}
                size="small"
                aria-label={`Remove account ${index + 1}`}
              >
                <RemoveIcon />
              </IconButton>
            </Box>
          )}
        </Box>
      ))}
      <HPButton
        variant="outlined"
        size="small"
        className="normal-case mt-4"
        startIcon={<PlusIconBlue />}
        onClick={handleAddAccount}
      >
        Add account
      </HPButton>
    </Box>
  );
};

export default InviteNewAccount;
